import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { useFetch } from "../../lib/useFetch";
import { DepartureRow, DocsBadge, FundedBadge, Panel, Stat } from "./shared";

/** Ops home (Phase C): departures with funding + doc state, bookings needing a
 *  check. The payments-inbox block arrives with the Phase D money screens. */

type OpsHomeData = {
  stats: { departures14: number; departures14Unfunded: number; bookingsToCheck: number; unsignedUpcoming: number };
  departures: DepartureRow[];
};

/** Upcoming imported bookings with no yacht & base — the manual-link worklist
 *  left after auto-linking the certain matches (api/bookings/yacht-link.ts). */
type UnlinkedImported = {
  bookingId: string;
  brand: string | null;
  startDate: string | null;
  clientName: string | null;
  yachtName: string | null;
  supplier: string | null;
  declined: boolean;
};

export default function OpsHome() {
  const { fetch } = useFetch();
  const navigate = useNavigate();
  const [data, setData] = useState<OpsHomeData | null>(null);
  const [unlinked, setUnlinked] = useState<UnlinkedImported[] | null>(null);

  useEffect(() => {
    fetch<{ data: OpsHomeData }>("/api/offers/workspace/home-ops")
      .then((r) => setData(r.data))
      .catch(() => null);
    fetch<{ data: UnlinkedImported[] }>("/api/bookings/yacht-links/unlinked-upcoming")
      .then((r) => setUnlinked(r.data ?? []))
      .catch(() => setUnlinked([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Content title="Operations">
      <div className="flex flex-col gap-6 p-1">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label="Departures ≤14 days"
            value={data?.stats.departures14}
            detail={data ? `${data.stats.departures14Unfunded} not fully paid` : undefined}
            tone={data && data.stats.departures14Unfunded > 0 ? "bad" : "good"}
          />
          <Stat label="Bookings to check" value={data?.stats.bookingsToCheck} detail="new / changed bookings" />
          <Stat
            label="Contracts not signed"
            value={data?.stats.unsignedUpcoming}
            detail="departing ≤45 days"
            tone={data && data.stats.unsignedUpcoming > 0 ? "bad" : "good"}
          />
          <Stat
            label="Funding health"
            value={data ? (data.departures.some((d) => d.funded === false) ? "Gaps" : "On track") : undefined}
            detail={data ? `${data.departures.filter((d) => d.funded === false).length} charters awaiting balance` : undefined}
            tone={data && data.departures.some((d) => d.funded === false) ? "bad" : "good"}
          />
        </div>

        <Panel
          title="Upcoming departures (45 days)"
          action={
            <Link className="text-sm text-blue-600 hover:underline" to="/bookings">
              All bookings
            </Link>
          }
        >
          {!data ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : data.departures.length === 0 ? (
            <p className="text-sm text-slate-500">No departures in the next 45 days.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1.5 pr-3">Charter</th>
                  <th className="py-1.5 pr-3">Client</th>
                  <th className="py-1.5 pr-3">Departs</th>
                  <th className="py-1.5 pr-3">Funding</th>
                  <th className="py-1.5">Docs</th>
                </tr>
              </thead>
              <tbody>
                {data.departures.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => navigate(`/bookings/${d.id}?tab=overview`)}
                    className="cursor-pointer border-t hover:bg-slate-50"
                  >
                    <td className="py-2 pr-3 font-medium">{d.yachtName ?? "—"}</td>
                    <td className="py-2 pr-3">{d.clientName ?? "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                      {d.daysTo === 0 ? "today" : `in ${d.daysTo}d`}
                    </td>
                    <td className="py-2 pr-3">
                      <FundedBadge funded={d.funded} />
                    </td>
                    <td className="py-2">
                      <DocsBadge docs={d.docs} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel
          title={`Imported bookings to link${unlinked ? ` (${unlinked.length})` : ""}`}
          action={<span className="text-xs text-slate-400">no yacht & base yet, so no boarding information</span>}
        >
          <>
            {!unlinked ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : unlinked.length === 0 ? (
              <p className="text-sm text-slate-500">Every upcoming imported booking has its yacht & base.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1.5 pr-3">Deal yacht</th>
                    <th className="py-1.5 pr-3">Operator on the deal</th>
                    <th className="py-1.5 pr-3">Client</th>
                    <th className="py-1.5">Departs</th>
                  </tr>
                </thead>
                <tbody>
                  {unlinked.slice(0, 50).map((u) => (
                    <tr
                      key={u.bookingId}
                      onClick={() => navigate(`/bookings/${u.bookingId}`)}
                      className="cursor-pointer border-t hover:bg-slate-50"
                    >
                      <td className="py-2 pr-3 font-medium">
                        {u.yachtName ?? "—"}
                        {u.declined && (
                          <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">link removed</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{u.supplier ?? "—"}</td>
                      <td className="py-2 pr-3">{u.clientName ?? "—"}</td>
                      <td className="whitespace-nowrap py-2 tabular-nums">
                        {u.startDate
                          ? new Date(u.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {unlinked && unlinked.length > 50 && (
              <p className="mt-2 text-xs text-slate-400">Showing the 50 soonest of {unlinked.length}.</p>
            )}
          </>
        </Panel>
      </div>
    </Content>
  );
}
